/**
 * A notification the operating system draws, outside the browser.
 *
 * THE HONEST LIMIT FIRST, because it shapes everything here: a web page cannot
 * raise its own window above other applications. Nothing in a browser can.
 * What a page CAN do is own the whole Jarvis viewport with a modal, ring,
 * flash its tab title - and ask the operating system to draw a notification of
 * its own, which is the only one of those that appears when Jarvis is behind
 * another window entirely. That last one is what this file is.
 *
 * PERMISSION IS ASKED FOR AT THE RIGHT MOMENT, which is not page load.
 * A site that asks on arrival gets refused, and a refusal is permanent until
 * somebody digs into browser settings - so one badly-timed prompt costs the
 * feature for good. It is asked for when somebody presses Call: they have just
 * shown they intend to use calling, which is the only moment the question
 * makes sense. The answer arrives too late for that first call and is right
 * for every one after it.
 *
 * Everything degrades to nothing. No Notification API, permission refused, a
 * browser that throws on construction - all of them end with this doing
 * nothing at all, because the modal and the ring do not depend on it.
 */

import { popupFor } from './notifyPrefs';

const supported = () => typeof window !== 'undefined' && 'Notification' in window;

/** 'granted' | 'denied' | 'default' | 'unsupported' */
export const notifyState = () => (supported() ? Notification.permission : 'unsupported');

/**
 * Ask, once, at a moment that makes sense.
 *
 * Never asks again after a refusal: the browser would not show the prompt a
 * second time anyway, and pretending otherwise just produces dead code paths.
 */
export async function askToNotify() {
  if (!supported()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    // Older Safari takes a callback rather than returning a promise, and
    // throws on the promise form.
    return Notification.permission;
  }
}

let current = null;

/**
 * Show one.
 *
 * ONLY WHEN THE TAB IS HIDDEN, deliberately. An operating-system notification
 * for something already filling the screen in front of somebody is noise, and
 * noise is what gets notifications turned off.
 *
 * `tag` replaces rather than stacks: a call that rings for twenty seconds
 * should be one notification, not nine.
 */
/*
 * Two options, for the to-do reminders (batch 3):
 *
 *   `whenUnfocused` also shows one when the tab is visible but the window is
 *   not the one being used - Jarvis on the second screen, or behind the mail.
 *   A reminder is for somebody who is NOT looking at Jarvis, and "hidden"
 *   alone misses the commonest way of not looking at it.
 *
 *   `replace: false` leaves the current one alone. A reminder must not take
 *   down a ringing call's notification, nor be taken down by the next one.
 */
/*
 * `kind` (117) is the notification preference it belongs to - 'calls',
 * 'todo_reminders' - and a person who switched that kind's "sound & pop-up"
 * off on their Notifications page gets no pop-up for it.
 */
export function osNotify({ title, body, tag = 'jarvis', onClick, whenUnfocused = false, replace = true, kind = null } = {}) {
  if (!supported() || Notification.permission !== 'granted') return null;
  if (kind && !popupFor(kind)) return null;
  if (typeof document !== 'undefined' && !document.hidden) {
    let away = false;
    try { away = whenUnfocused && typeof document.hasFocus === 'function' && !document.hasFocus(); } catch { /* no */ }
    if (!away) return null;
  }
  try {
    if (replace) close();
    const n = new Notification(title, { body, tag, requireInteraction: true });
    n.onclick = () => {
      try { window.focus(); } catch { /* blocked, which is fine */ }
      if (onClick) onClick();
      try { n.close(); } catch { /* already gone */ }
    };
    if (replace) current = n;
    return n;
  } catch {
    // Chrome on Android refuses the constructor entirely and wants a service
    // worker. Nothing to do about that here, and nothing depends on it.
    return null;
  }
}

/** Take it away - answered, declined, or gone. */
export function close() {
  try { current?.close(); } catch { /* already gone */ }
  current = null;
}
